// The booking team's PRE-HOLD SHORTLIST — the WRAPPER layer (lint-exempt for TanStack Query).
//
// An OFFER is a candidate date the agent is chasing, before any of it is a hold: who is asking, for how much,
// and how long the offer stands. It is band-scoped (`artist.booking_offers`, Class-A RLS), so the key is the
// tenant-keyed ['band', bandId, 'booking', 'offers'] and every hook builds on useBandQuery/useBandMutation.
//
// It is NOT a show. Converting one opens the show editor's CREATE flow seeded from the offer; the hold is born
// from the editor's own save, and {@link useConvertBookingOffer} then records what it became. So nothing here
// writes to the schedule spine, and an offer cannot appear on the band's calendar.
//
// Reachable only where `offers:manage` is held — Booking-root org seats under the roster lens. Callers gate the
// AFFORDANCE on the same capability rather than rendering a control that 403s (PO, 2026-09-02).
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError, errorMessage } from "./apiError.js";
import { pushToast } from "../toasts.js";

export type BookingOfferDto = components["schemas"]["BookingOfferDto"];
export type BookingOffersResponse = components["schemas"]["BookingOffersResponse"];
export type BookingOfferRequest = components["schemas"]["BookingOfferRequest"];

/**
 * The free-text ceiling on an offer's details, mirroring `HasMaxLength(4000)` on `BookingOffer.Details`. It
 * lives here so the textarea can stop AT the limit rather than let someone paste a page the save refuses.
 * Not a business value — a storage bound.
 */
export const OFFER_DETAILS_MAX = 4000;

/** The band-scoped shortlist key. Every mutation below invalidates exactly this. */
export const bookingOffersKey = (bandId: string) => ["band", bandId, "booking", "offers"] as const;

/**
 * `GET /bands/{bandId}/booking/offers` — the band's OPEN shortlist, soonest date first.
 *
 * `enabled` lets the caller defer the fetch behind its own capability gate, so a lens without
 * `offers:manage` fires no read at all rather than a 403 the UI then has to explain away.
 */
export function useBookingOffers(opts: { enabled?: boolean } = {}) {
  const { api } = useApp();
  return useBandQuery<BookingOffersResponse>(
    ["booking", "offers"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/booking/offers", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 30_000, enabled: opts.enabled ?? true },
  );
}

/** Invalidate the shortlist. Shared by every mutation here AND by the convert flow's caller, which lands
 *  back on the board after the editor created the hold.
 *
 *  It ALSO invalidates the cross-roster overview (`['org', …, 'roster', 'overview']`), whose Offer column is
 *  the roster-wide ROLLUP of these same shortlists (ART-139). Without it, converting/passing/creating an
 *  offer on the per-artist board leaves the scope page's Offer column stale on return — the server recomputes
 *  (the offer service busts its own cache), but the client never refetches. A predicate over every org
 *  overview because any org reaching this band rolls the offer up; it is a cheap client-side mark. */
function useInvalidateOffers() {
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: bookingOffersKey(bandId) });
    void qc.invalidateQueries({
      predicate: (q) => q.queryKey[0] === "org" && q.queryKey[2] === "roster" && q.queryKey[3] === "overview",
    });
  };
}

/**
 * `POST /bands/{bandId}/booking/offers` (201 → BookingOfferDto) — add a shortlist row.
 * Server-confirm then repaint (NOT optimistic — a create mints a server id).
 */
export function useCreateBookingOffer() {
  const { api } = useApp();
  const invalidate = useInvalidateOffers();

  return useBandMutation<BookingOfferDto, BookingOfferRequest>(
    async (band, body) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/booking/offers", {
        params: { path: { bandId: band } },
        body,
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: invalidate,
      onError: (err) => pushToast(errorMessage(err, "Couldn't create the offer")),
    },
  );
}

/** `PUT /booking/offers/{offerId}` — overwrite a shortlist row (single-editor last-write). 409 once the
 *  offer has been converted, which the toast surfaces verbatim. */
export function useUpdateBookingOffer() {
  const { api } = useApp();
  const invalidate = useInvalidateOffers();

  return useBandMutation<BookingOfferDto, { offerId: string; body: BookingOfferRequest }>(
    async (band, { offerId, body }) => {
      const { data, error, response } = await api.PUT("/api/v1/bands/{bandId}/booking/offers/{offerId}", {
        params: { path: { bandId: band, offerId } },
        body,
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: invalidate,
      onError: (err) => pushToast(errorMessage(err, "Couldn't save the offer")),
    },
  );
}

/** `POST /booking/offers/{offerId}/pass` — we said no. The row is kept, not deleted; it simply leaves the
 *  open shortlist the list endpoint serves. */
export function usePassBookingOffer() {
  const { api } = useApp();
  const invalidate = useInvalidateOffers();

  return useBandMutation<BookingOfferDto, string>(
    async (band, offerId) => {
      const { data, error, response } = await api.POST(
        "/api/v1/bands/{bandId}/booking/offers/{offerId}/pass",
        { params: { path: { bandId: band, offerId } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: invalidate,
      onError: (err) => pushToast(errorMessage(err, "Couldn't pass on this offer")),
    },
  );
}

/**
 * `POST /booking/offers/{offerId}/convert` — record that this offer became the given hold.
 *
 * Called AFTER the show editor's create has landed, with the id it returned. The server does not create the
 * show: the hold is exactly what the agent saved in the editor, and this call is what stops the shortlist
 * drawing a card beside the hold it turned into.
 *
 * A failure here is worth surfacing rather than swallowing — the hold exists either way, so the visible
 * symptom would be a duplicate card in the Offer column with no explanation.
 */
export function useConvertBookingOffer() {
  const { api } = useApp();
  const invalidate = useInvalidateOffers();

  return useBandMutation<BookingOfferDto, { offerId: string; showId: string }>(
    async (band, { offerId, showId }) => {
      const { data, error, response } = await api.POST(
        "/api/v1/bands/{bandId}/booking/offers/{offerId}/convert",
        { params: { path: { bandId: band, offerId } }, body: { showId } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: invalidate,
      onError: (err) =>
        pushToast(errorMessage(err, "The hold was created, but we couldn't clear the offer from the board")),
    },
  );
}

/** `DELETE /booking/offers/{offerId}` (204) — remove a row typed in by mistake. 409 once converted. */
export function useDeleteBookingOffer() {
  const { api } = useApp();
  const invalidate = useInvalidateOffers();

  return useBandMutation<void, string>(
    async (band, offerId) => {
      const { error, response } = await api.DELETE("/api/v1/bands/{bandId}/booking/offers/{offerId}", {
        params: { path: { bandId: band, offerId } },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onSuccess: invalidate,
      onError: (err) => pushToast(errorMessage(err, "Couldn't delete the offer")),
    },
  );
}

// ---- the booking deal flow (ART-326) -------------------------------------------------------------------

export type SendToTeamRequest = components["schemas"]["SendToTeamRequest"];
export type ShowRequestDetails = components["schemas"]["ShowRequestDetails"];
export type ShowRequestSlot = components["schemas"]["ShowRequestSlot"];
export type DealPerson = components["schemas"]["DealPerson"];

/** `POST /booking/offers/{offerId}/send` — send a soft hold to the team as a show request (tells Mgmt + the artist
 *  AV), or save the edit to a request already sent ("Save changes"; tells nobody again). */
export function useSendToTeam() {
  const { api } = useApp();
  const invalidate = useInvalidateOffers();
  return useBandMutation<BookingOfferDto, { offerId: string; body: SendToTeamRequest }>(
    async (band, { offerId, body }) => {
      const { data, error, response } = await api.POST(
        "/api/v1/bands/{bandId}/booking/offers/{offerId}/send",
        { params: { path: { bandId: band, offerId } }, body },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: invalidate,
      onError: (err) => pushToast(errorMessage(err, "Couldn't send this to the team")),
    },
  );
}

/** `POST /booking/offers/release-others` — release the agency's other soft holds on the same night. */
export function useReleaseOtherSoftHolds() {
  const { api } = useApp();
  const invalidate = useInvalidateOffers();
  return useBandMutation<void, string>(
    async (band, keptOfferId) => {
      const { error, response } = await api.POST("/api/v1/bands/{bandId}/booking/offers/release-others", {
        params: { path: { bandId: band } },
        body: { keptOfferId },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onSuccess: invalidate,
      onError: (err) => pushToast(errorMessage(err, "Couldn't release the other soft holds")),
    },
  );
}

/** `GET /booking/offers/expired` — deals that fell off past their Good through (recoverable). */
export function useExpiredBookingOffers(opts: { enabled?: boolean } = {}) {
  const { api } = useApp();
  return useBandQuery<BookingOffersResponse>(
    ["booking", "offers", "expired"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/booking/offers/expired", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 30_000, enabled: opts.enabled ?? true },
  );
}

/** `POST /booking/offers/{offerId}/restore` — put an expired deal (and its hold) back on the board. */
export function useRestoreBookingOffer() {
  const { api } = useApp();
  const invalidate = useInvalidateOffers();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<BookingOfferDto, string>(
    async (band, offerId) => {
      const { data, error, response } = await api.POST(
        "/api/v1/bands/{bandId}/booking/offers/{offerId}/restore",
        { params: { path: { bandId: band, offerId } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: () => {
        invalidate();
        void qc.invalidateQueries({ queryKey: ["band", bandId, "booking"] });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't restore this deal")),
    },
  );
}
